import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { MessageSquareText, Plus, Heart, MessageCircle, Share2, Loader2, MoreVertical, User, X } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { format } from 'date-fns';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { CreatePost } from './CreatePost';
import { EditPostDialog } from './EditPostDialog';
import { subscribeToGroupPosts, deletePost, togglePostLike } from '@/lib/firebase/queries/posts';
import { hasLiked } from '@/lib/groups/groupPosts';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { Group, Post } from '@/types/support';
import { cn } from '@/lib/utils';

interface GroupPostsDialogProps {
  group: Group;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GroupPostsDialog({ group, open, onOpenChange }: GroupPostsDialogProps) {
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [postToEdit, setPostToEdit] = useState<Post | null>(null);
  const [postToDelete, setPostToDelete] = useState<Post | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { t, language } = useLanguage();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!open || !group.id) return;

    const unsubscribe = subscribeToGroupPosts(
      group.id,
      (updatedPosts) => {
        setPosts(updatedPosts);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading posts:', error);
        toast.error(t('support.postsLoadError'));
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [open, group.id]);

  const handleCreatePostSuccess = () => {
    setShowCreatePost(false);
  };

  const handleDeletePost = async () => {
    if (!postToDelete) return;

    try {
      setIsDeleting(true);
      await deletePost(postToDelete.id);
      toast.success(t('support.postDeleted'));
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error(t('support.postDeleteError'));
    } finally {
      setIsDeleting(false);
      setPostToDelete(null);
    }
  };

  const handleLikePost = async (post: Post) => {
    if (!currentUser) {
      toast.error(t('support.loginRequired'));
      return;
    }

    try {
      await togglePostLike(post.id, currentUser.uid);
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error(t('support.likeError'));
    }
  };

  // Check if user can modify a post
  const canModifyPost = (post: Post) => {
    if (!currentUser) return false;
    // Allow if user is the author of the post
    if (post.userId === currentUser.uid) return true;
    // Allow if user is an editor of the group
    if (group.editors?.includes(currentUser.uid)) return true;
    return false;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-screen h-[100dvh] sm:w-[95vw] sm:max-w-6xl sm:h-[85vh] flex flex-col p-0"
        hideCloseButton
        dir={language === 'ar' ? 'rtl' : 'ltr'}
      >
        <div className="flex flex-col h-full relative">
          <DialogHeader className="px-4 sm:px-6 py-3 sm:py-4 border-b">
            {/* Mobile Header */}
            <div className="flex flex-col gap-3 sm:hidden">
              <div className="flex items-center justify-between">
                <DialogTitle className="text-lg flex items-center gap-2">
                  <MessageSquareText className="h-5 w-5 text-[#7dda55]" />
                  {t('support.groupPosts')}
                </DialogTitle>
                <DialogClose asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('common.close')}
                    className="h-8 w-8"
                  >
                    <span className="sr-only">{t('common.close')}</span>
                    <X className="h-4 w-4" />
                  </Button>
                </DialogClose>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  {group.name} • {Math.max(Array.isArray(group.members) ? group.members.length : 1, 1)} {t('support.members')}
                </div>
                <Button 
                  onClick={() => setShowCreatePost(true)}
                  className="bg-[#7dda55] hover:bg-[#6dc447] text-white"
                  size="sm"
                >
                  <Plus className="h-4 w-4 me-1" />
                  {t('support.createPost')}
                </Button>
              </div>
            </div>

            {/* Desktop Header */}
            <div className="hidden sm:flex sm:items-center sm:gap-2 sm:relative">
              <DialogTitle className="text-xl flex items-center gap-2 text-left">
                <MessageSquareText className="h-5 w-5 text-[#7dda55]" />
                {t('support.groupPosts')}
              </DialogTitle>
              <DialogClose asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('common.close')}
                  className={cn(
                    'absolute top-1/2 -translate-y-1/2',
                    language === 'ar' ? 'left-0' : 'right-0'
                  )}
                >
                  <span className="sr-only">{t('common.close')}</span>
                  <X className="h-5 w-5" />
                </Button>
              </DialogClose>
            </div>
          </DialogHeader>

          <div className="flex flex-1 overflow-hidden">
            {/* Desktop Sidebar */}
            <div className={cn(
              "hidden sm:flex w-64 bg-muted/40 flex-col gap-4 h-full",
              language === 'ar' ? "border-l" : "border-r"
            )}>
              <div className="p-4">
                <Button 
                  onClick={() => setShowCreatePost(true)}
                  className="bg-[#7dda55] hover:bg-[#6dc447] text-white w-full justify-start"
                  size="sm"
                >
                  <Plus className={cn("h-4 w-4", language === 'ar' ? "ml-2" : "mr-2")} />
                  {t('support.createPost')}
                </Button>
              </div>

              <div className="px-4 space-y-2">
                <div className="space-y-1 text-sm">
                  <p className="font-medium">{group.name}</p>
                  <p className="text-muted-foreground">{group.description}</p>
                </div>
              </div>

              <div className="px-4 space-y-2 mt-auto mb-4">
                <h3 className="text-sm font-medium text-muted-foreground">
                  {t('support.members')}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {Math.max(Array.isArray(group.members) ? group.members.length : 1, 1)} {t('support.members')}
                </p>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-y-auto bg-background/95 backdrop-blur">
              <div className="p-3 sm:p-6">
                {loading ? (
                  <div className="flex items-center justify-center h-40">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : posts.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {t('support.noPosts')}
                  </div>
                ) : (
                  <div className="space-y-4 sm:space-y-6">
                    {posts.map((post) => (
                      <div 
                        key={post.id}
                        className="rounded-lg border bg-card p-4 sm:p-6"
                        dir={language === 'ar' ? 'rtl' : 'ltr'}
                      >
                        <div className="flex items-start justify-between gap-3 sm:gap-4 mb-3 sm:mb-4">
                          <div className="flex items-start gap-2 sm:gap-3 min-w-0 flex-1">
                            <Avatar className="h-8 w-8 sm:h-12 sm:w-12 mt-1 sm:mt-[5px] flex-shrink-0">
                              <AvatarImage src={post.userPhotoURL} alt={post.userName} />
                              <AvatarFallback className="bg-[#7dda55] text-white">
                                <User className="h-3 w-3 sm:h-5 sm:w-5" />
                              </AvatarFallback>
                            </Avatar>
                            <div className="space-y-1 min-w-0 flex-1">
                              <h3 className="font-semibold text-base sm:text-lg leading-tight">{post.title}</h3>
                              <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-muted-foreground">
                                <span className="truncate">{format(post.createdAt, 'MMM d, h:mm a')}</span>
                                <span className="hidden sm:inline">•</span>
                                <span className="font-medium truncate hidden sm:inline">
                                  {language === 'ar'
                                    ? post.userNameAr || post.userName
                                    : post.userNameEn || post.userName}
                                </span>
                              </div>
                            </div>
                          </div>

                          {canModifyPost(post) && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0">
                                  <MoreVertical className="h-3 w-3 sm:h-4 sm:w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align={language === 'ar' ? 'start' : 'end'}>
                                <DropdownMenuItem onClick={() => setPostToEdit(post)}>
                                  {t('support.editPost')}
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => setPostToDelete(post)}
                                  className="text-red-600"
                                >
                                  {t('support.deletePost')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>

                        <p className="text-sm sm:text-base text-muted-foreground mb-3 sm:mb-4 whitespace-pre-wrap leading-relaxed">{post.content}</p>

                        <div className="flex items-center gap-2 sm:gap-4">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className={cn("gap-1 sm:gap-2 h-8 px-2 sm:px-3", hasLiked(post, currentUser?.uid) && "text-red-500")}
                            onClick={() => handleLikePost(post)}
                          >
                            <Heart className={cn("h-3 w-3 sm:h-4 sm:w-4", hasLiked(post, currentUser?.uid) && "fill-current")} />
                            <span className="text-xs sm:text-sm">{Math.max(0, post.likes || 0)}</span>
                          </Button>
                          <Button variant="ghost" size="sm" className="gap-1 sm:gap-2 h-8 px-2 sm:px-3">
                            <MessageCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                            <span className="text-xs sm:text-sm">{post.comments || 0}</span>
                          </Button>
                          <Button variant="ghost" size="sm" className="gap-1 sm:gap-2 h-8 px-2 sm:px-3">
                            <Share2 className="h-3 w-3 sm:h-4 sm:w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>

      {showCreatePost && (
        <CreatePost
          group={group}
          open={showCreatePost}
          onOpenChange={setShowCreatePost}
          onSuccess={handleCreatePostSuccess}
        />
      )}

      {postToEdit && (
        <EditPostDialog
          post={postToEdit}
          open={!!postToEdit}
          onOpenChange={(open) => !open && setPostToEdit(null)}
        />
      )}

      <AlertDialog open={!!postToDelete} onOpenChange={(open) => !open && setPostToDelete(null)}>
        <AlertDialogContent dir={language === 'ar' ? 'rtl' : 'ltr'}>
          <AlertDialogHeader className={language === 'ar' ? 'text-right pr-0' : 'text-left pl-0'}>
            <AlertDialogTitle className={language === 'ar' ? 'text-right' : 'text-left'}>{t('support.deletePostTitle')}</AlertDialogTitle>
            <AlertDialogDescription className={language === 'ar' ? 'text-right' : 'text-left'}>
              {t('support.deletePostDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={`${language === 'ar' ? 'flex-row-reverse' : ''} gap-2`}>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePost}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className={cn("h-4 w-4 animate-spin", language === 'ar' ? "ml-2" : "mr-2")} />
                  {t('common.deleting')}
                </>
              ) : (
                t('common.delete')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}